/**
 * Pure geometry for logo sizing and optical transforms.
 *
 * The parent only has iframe-reported rectangles, so this module deliberately
 * has no DOM, store or bridge dependency. The editor, drag handles and browser
 * fidelity gate can therefore use the same limits instead of copying them.
 */

export const LOGO_MIN_SCALE = 0.5;
export const LOGO_MAX_SCALE = 2;
export const LOGO_SAFETY_PX = 8;

function rectValue(rect, primary, fallback) {
  const value = Number(rect?.[primary] ?? rect?.[fallback]);
  return Number.isFinite(value) ? value : null;
}

export function isUsableLogoRect(rect) {
  return (
    !!rect &&
    rectValue(rect, 'x', 'left') != null &&
    rectValue(rect, 'y', 'top') != null &&
    Number.isFinite(Number(rect.width)) &&
    Number.isFinite(Number(rect.height)) &&
    Number(rect.width) > 0 &&
    Number(rect.height) > 0
  );
}

export function logoRectParts(rect) {
  const x = rectValue(rect, 'x', 'left');
  const y = rectValue(rect, 'y', 'top');
  const width = Number(rect.width);
  const height = Number(rect.height);
  return { x, y, width, height, right: x + width, bottom: y + height };
}

export function logoAspectNumber(value) {
  if (Number.isFinite(Number(value)) && Number(value) > 0) return Number(value);
  const match = /^\s*([\d.]+)\s*\/\s*([\d.]+)\s*$/.exec(String(value || ''));
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  return width > 0 && height > 0 ? width / height : null;
}

export function staticLogoClamp(scale) {
  const numericScale = Number(scale);
  const safeScale = Number.isFinite(numericScale) ? numericScale : 1;
  return Math.max(0.05, 0.5 - Math.max(0, safeScale - 1) * 0.25);
}

/** Translation limits for the painted mark at one optical scale. */
export function clampLogoTranslate(rects = {}, scale = 1) {
  const fallback = staticLogoClamp(scale);
  if (!isUsableLogoRect(rects.logoSlotRect) || !isUsableLogoRect(rects.logoBoundaryRect)) {
    return {
      minTx: -fallback,
      maxTx: fallback,
      minTy: -fallback,
      maxTy: fallback,
      fallback,
      usedFallback: true,
      collapsedX: false,
      collapsedY: false
    };
  }

  const slot = logoRectParts(rects.logoSlotRect);
  const boundary = logoRectParts(rects.logoBoundaryRect);
  const numericScale = Number.isFinite(Number(scale)) ? Number(scale) : 1;
  const extraW = (slot.width * Math.max(0, numericScale - 1)) / 2;
  const extraH = (slot.height * Math.max(0, numericScale - 1)) / 2;

  let leftLimit = boundary.x + LOGO_SAFETY_PX;
  let rightLimit = boundary.right - LOGO_SAFETY_PX;
  let topLimit = boundary.y + LOGO_SAFETY_PX;
  let bottomLimit = boundary.bottom - LOGO_SAFETY_PX;
  let hasLeftBlocker = false;
  let hasRightBlocker = false;
  let hasTopBlocker = false;
  let hasBottomBlocker = false;

  for (const raw of Array.isArray(rects.logoBlockerRects) ? rects.logoBlockerRects : []) {
    if (!isUsableLogoRect(raw)) continue;
    const blocker = logoRectParts(raw);

    if (blocker.x >= slot.x && blocker.x < rightLimit) {
      rightLimit = blocker.x - LOGO_SAFETY_PX;
      hasRightBlocker = true;
    }
    if (blocker.right <= slot.right && blocker.right > leftLimit) {
      leftLimit = blocker.right + LOGO_SAFETY_PX;
      hasLeftBlocker = true;
    }
    if (blocker.y >= slot.y && blocker.y < bottomLimit) {
      bottomLimit = blocker.y - LOGO_SAFETY_PX;
      hasBottomBlocker = true;
    }
    if (blocker.bottom <= slot.bottom && blocker.bottom > topLimit) {
      topLimit = blocker.bottom + LOGO_SAFETY_PX;
      hasTopBlocker = true;
    }
  }

  let minTx = (leftLimit - slot.x + extraW) / slot.width;
  let maxTx = (rightLimit - slot.x - slot.width - extraW) / slot.width;
  let minTy = (topLimit - slot.y + extraH) / slot.height;
  let maxTy = (bottomLimit - slot.y - slot.height - extraH) / slot.height;

  if (![minTx, maxTx, minTy, maxTy].every(Number.isFinite)) {
    return {
      minTx: -fallback,
      maxTx: fallback,
      minTy: -fallback,
      maxTy: fallback,
      fallback,
      usedFallback: true,
      collapsedX: false,
      collapsedY: false
    };
  }

  minTx = Math.max(minTx, -fallback);
  maxTx = Math.min(maxTx, fallback);
  minTy = Math.max(minTy, -fallback);
  maxTy = Math.min(maxTy, fallback);

  const collapsedX = minTx > maxTx;
  const collapsedY = minTy > maxTy;
  if (collapsedX) {
    const resolved =
      hasRightBlocker && !hasLeftBlocker
        ? maxTx
        : hasLeftBlocker && !hasRightBlocker
          ? minTx
          : Math.max(-fallback, Math.min(fallback, (minTx + maxTx) / 2));
    minTx = resolved;
    maxTx = resolved;
  }
  if (collapsedY) {
    const resolved =
      hasBottomBlocker && !hasTopBlocker
        ? maxTy
        : hasTopBlocker && !hasBottomBlocker
          ? minTy
          : Math.max(-fallback, Math.min(fallback, (minTy + maxTy) / 2));
    minTy = resolved;
    maxTy = resolved;
  }

  return {
    minTx,
    maxTx,
    minTy,
    maxTy,
    fallback,
    usedFallback: false,
    collapsedX,
    collapsedY
  };
}

function clamp(value, min, max) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return min;
  return Math.max(min, Math.min(max, numeric));
}

/**
 * Maximum anchor/slot height that fits the current header band and blockers.
 * Width is derived from the asset ratio; the anchor remains on its current
 * side of the band so flex/grid alignment can continue to position it.
 */
export function logoSizeLimits(rects = {}, aspectRatio = null) {
  const ratio = logoAspectNumber(aspectRatio);
  const slot = isUsableLogoRect(rects.logoSlotRect)
    ? logoRectParts(rects.logoSlotRect)
    : isUsableLogoRect(rects.rect)
      ? logoRectParts(rects.rect)
      : null;
  const boundary = isUsableLogoRect(rects.logoBoundaryRect)
    ? logoRectParts(rects.logoBoundaryRect)
    : isUsableLogoRect(rects.rect)
      ? logoRectParts(rects.rect)
      : null;

  if (!slot || !boundary || !ratio) {
    return { minPx: 1, maxPx: Infinity, usableHeightPx: null, usableWidthPx: null };
  }

  // Preserve the box that already fits. A fixed 8px inset on every edge turns
  // a legitimate 100x14 compact-header logo into a 9x1 speck during replace.
  // Spend safety space only where the band actually has space outside the
  // current slot; a roomier header still receives the full collision gutter.
  const leftSafety = Math.min(LOGO_SAFETY_PX, Math.max(0, slot.x - boundary.x));
  const rightSafety = Math.min(LOGO_SAFETY_PX, Math.max(0, boundary.right - slot.right));
  const topSafety = Math.min(LOGO_SAFETY_PX, Math.max(0, slot.y - boundary.y));
  const bottomSafety = Math.min(LOGO_SAFETY_PX, Math.max(0, boundary.bottom - slot.bottom));
  let left = boundary.x + leftSafety;
  let right = boundary.right - rightSafety;
  const slotCenterY = slot.y + slot.height / 2;

  for (const raw of Array.isArray(rects.logoBlockerRects) ? rects.logoBlockerRects : []) {
    if (!isUsableLogoRect(raw)) continue;
    const blocker = logoRectParts(raw);
    if (slotCenterY < blocker.y || slotCenterY > blocker.bottom) continue;
    if (blocker.x >= slot.x && blocker.x < right) right = blocker.x - LOGO_SAFETY_PX;
    if (blocker.right <= slot.right && blocker.right > left) {
      left = blocker.right + LOGO_SAFETY_PX;
    }
  }

  const boundaryCenterX = boundary.x + boundary.width / 2;
  const slotCenterX = slot.x + slot.width / 2;
  const usableWidthPx = Math.max(
    1,
    slotCenterX <= boundaryCenterX
      ? right - Math.max(left, slot.x)
      : Math.min(right, slot.right) - left
  );
  const usableHeightPx = Math.max(1, boundary.height - topSafety - bottomSafety);
  const maxPx = Math.max(1, Math.min(usableHeightPx, usableWidthPx / ratio));
  return {
    // A very wide mark in a compact anchor can legitimately have a maximum
    // below the normal 12px interaction floor. Never publish min > max: the
    // generic clamp would then grow past the box it is supposed to protect.
    minPx: Math.min(12, maxPx),
    maxPx,
    usableHeightPx,
    usableWidthPx
  };
}

export function clampLogoHeightPx(requestedPx, rects = {}, aspectRatio = null) {
  const requested = Number(requestedPx);
  if (!Number.isFinite(requested) || requested <= 0) {
    return { px: requested, maxPx: Infinity, percent: null, clamped: false };
  }
  const limits = logoSizeLimits(rects, aspectRatio);
  const px = clamp(requested, limits.minPx, limits.maxPx);
  return {
    ...limits,
    px,
    percent: limits.usableHeightPx ? (px / limits.usableHeightPx) * 100 : null,
    clamped: Math.abs(px - requested) > 0.01
  };
}

/** Keep the current visual height when possible, shrinking only to fit width. */
export function fitLogoHeightPx(rects = {}, aspectRatio = null) {
  const current = Number(rects?.logoSlotRect?.height || rects?.rect?.height);
  if (!Number.isFinite(current) || current <= 0) return null;
  return clampLogoHeightPx(current, rects, aspectRatio);
}

/** Existing element-override shape used for a logo anchor sized by height. */
export function logoAnchorOverrideForHeight(cssHeight = '') {
  const height = String(cssHeight || '').trim();
  return height
    ? { width: 'auto', height, maxWidth: '100%' }
    : { width: '', height: '', maxWidth: '' };
}

function scaleFits(rects, scale, tx, ty) {
  const limits = clampLogoTranslate(rects, scale);
  if (limits.usedFallback) return true;
  if (limits.collapsedX || limits.collapsedY) return false;
  return (
    tx >= limits.minTx - 0.0001 &&
    tx <= limits.maxTx + 0.0001 &&
    ty >= limits.minTy - 0.0001 &&
    ty <= limits.maxTy + 0.0001
  );
}

/** Clamp optical scale without ever rewriting the user's translation. */
export function clampLogoOpticalScale(rects = {}, requestedScale = 1, tx = 0, ty = 0) {
  const desired = clamp(requestedScale, LOGO_MIN_SCALE, LOGO_MAX_SCALE);
  const currentTx = Number.isFinite(Number(tx)) ? Number(tx) : 0;
  const currentTy = Number.isFinite(Number(ty)) ? Number(ty) : 0;
  if (scaleFits(rects, desired, currentTx, currentTy)) {
    return { scale: desired, clamped: desired !== Number(requestedScale) };
  }

  let low = LOGO_MIN_SCALE;
  let high = desired;
  if (!scaleFits(rects, low, currentTx, currentTy)) {
    return { scale: low, clamped: true };
  }
  for (let i = 0; i < 24; i += 1) {
    const mid = (low + high) / 2;
    if (scaleFits(rects, mid, currentTx, currentTy)) low = mid;
    else high = mid;
  }
  return { scale: Number(low.toFixed(4)), clamped: true };
}
